import { useContext, useEffect, useState } from 'react';
import { ThemeContext } from './ThemeProvider.js';
import type { DensityId, ThemeContextValue } from './types.js';

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}

export function useDensity(): DensityId {
  return useTheme().density;
}

/** Reduced-motion preference. Falls back to OS `prefers-reduced-motion`
 *  when no ThemeProvider is mounted, so primitives that use this hook
 *  (icons, spinners) remain usable in tests or standalone embeds. */
export function useReducedMotion(): boolean {
  const ctx = useContext(ThemeContext);
  const motion = ctx?.motion ?? 'standard';
  const [osReduced, setOsReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (): void => {
      setOsReduced(mq.matches);
    };
    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, []);
  return motion === 'reduced' || osReduced;
}
